import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close, Science, Public, Category, LocalOffer } from '@mui/icons-material';
import { motion } from 'framer-motion';
import AtomAnimation from './AtomAnimation';

/**
 * MineralDetailModal displays detailed information about a mineral
 * with animations and scientific data
 */
const MineralDetailModal = ({ open, onClose, mineral }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (!mineral) return null;

  // Generate a color based on the mineral's category
  const getCategoryColor = (category) => {
    const categoryColors = {
      'Transition Metal': '#3b82f6', // blue
      'Alkali Metal': '#ef4444',     // red
      'Post-transition Metal': '#8b5cf6', // purple
      'Lanthanides': '#ec4899',      // pink
      'Non-metal': '#10b981',        // green
      'default': '#6b7280'           // gray
    };

    return categoryColors[category] || categoryColors.default;
  };

  const mineralColor = getCategoryColor(mineral.category);

  // Calculate number of electrons based on atomic number
  const getElectrons = (atomicNumber) => {
    // For simplicity, just use the atomic number if it's a single value
    if (typeof atomicNumber === 'number') {
      return Math.min(atomicNumber, 8); // Limit to 8 for visual clarity
    }
    // For ranges like "57-71" for rare earth elements, use a default
    return 6;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: mineralColor,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" component="span">
            {mineral.name}
          </Typography>
          <Chip
            label={mineral.symbol}
            size="small"
            sx={{
              ml: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 'bold'
            }}
            icon={<Science sx={{ color: 'white !important' }} />}
          />
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Atomic Animation - Full Width */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Atomic Structure of {mineral.name}
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <AtomAnimation
                  symbol={mineral.symbol}
                  electrons={getElectrons(mineral.atomicNumber)}
                  color={mineralColor}
                  size={280}
                  showDetails={true}
                  name={mineral.name}
                  atomicNumber={mineral.atomicNumber}
                />
              </Box>
            </Box>
          </Grid>

          {/* Details */}
          <Grid item xs={12}>
            <Box sx={{ p: 2 }}>

              <Divider sx={{ my: 2 }} />

              {/* Properties */}
              <Typography variant="h6" gutterBottom>
                Properties
              </Typography>
              <Typography variant="body2" paragraph>
                {mineral.properties}
              </Typography>

              {/* Uses */}
              <Typography variant="h6" gutterBottom>
                Primary Uses
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {mineral.uses.map((use, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Chip
                      label={use}
                      icon={<LocalOffer />}
                      variant="outlined"
                      size="small"
                    />
                  </motion.div>
                ))}
              </Box>

              {/* Sources */}
              <Typography variant="h6" gutterBottom>
                Major Sources
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {mineral.sources.map((source, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + (index * 0.1) }}
                  >
                    <Chip
                      label={source}
                      icon={<Public />}
                      variant="outlined"
                      size="small"
                    />
                  </motion.div>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Category */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Category sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Category: {mineral.category}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MineralDetailModal;
